import _slicedToArray from "@babel/runtime/helpers/slicedToArray";
import { parents, isElementInViewport, HTML_ATTRIBUTE_BLOCKER_CONNECTED, HTML_ATTRIBUTE_WAS_PREVIOUSLY_STRICT_HIDDEN, HTML_ATTRIBUTE_BLOCKER_CONNECTED_PRESERVED, HTML_ATTRIBUTE_VISUAL_PAINT_MODE } from "..";
import { probablyResetParentContainerForVisual, isPotentialVisualWithinResponsiveContainer, findVisualParent, findPreviousVisibleDuplicate, createVisualContainer } from ".";
/**
 * Save the identifier for the last clicked content blocker so we can
 * dispatch in our event if it is the clicked one. This is useful e.g.
 * to delegate the click event further and start a video.
 */

var lastClickedConnectedCounter = undefined;
var connectedCounterPreserve = 0;
/**
 * Create visual content blocker for a given node.
 */

function createVisual(_ref) {
  var _node$style, _document$querySelect;

  var node = _ref.node,
      blocker = _ref.blocker,
      setVisualParentIfClassOfParent = _ref.setVisualParentIfClassOfParent,
      dependantVisibilityContainers = _ref.dependantVisibilityContainers,
      mount = _ref.mount;

  // Short circuit: We do not know a blocker for this; do nothing
  if (!blocker) {
    return;
  } // Apply a unique identifier for this node


  if (!node.hasAttribute(HTML_ATTRIBUTE_BLOCKER_CONNECTED_PRESERVED)) {
    node.setAttribute(HTML_ATTRIBUTE_BLOCKER_CONNECTED_PRESERVED, connectedCounterPreserve.toString());
    connectedCounterPreserve++;
  }

  var connectedCounter = +node.getAttribute(HTML_ATTRIBUTE_BLOCKER_CONNECTED_PRESERVED);
  var parentElement = node.parentElement;
  var _blocker$forceHidden = blocker.forceHidden,
      forceHidden = _blocker$forceHidden === void 0 ? false : _blocker$forceHidden,
      visual = blocker.visual,
      blockerId = blocker.id;
  var position = ((_node$style = node.style) === null || _node$style === void 0 ? void 0 : _node$style.position) || "initial"; // Visual content blockers are only allowed for "real" content

  var isPositioned = ["fixed", "absolute", "sticky"].indexOf(position) > -1;
  var isNotInContent = [document.body, document.head, document.querySelector("html")].indexOf(parentElement) > -1;
  var isAlreadyConnected = node.getAttribute(HTML_ATTRIBUTE_BLOCKER_CONNECTED);

  var _findVisualParent = findVisualParent(node, setVisualParentIfClassOfParent || {}),
      _findVisualParent2 = _slicedToArray(_findVisualParent, 2),
      createBefore = _findVisualParent2[0],
      visualParentMode = _findVisualParent2[1]; // const isInlineScriptOrStyle = createBefore.hasAttribute(HTML_ATTRIBUTE_INLINE) || createBefore.hasAttribute(HTML_ATTRIBUTE_INLINE_STYLE);


  var isVisible = !!createBefore.offsetParent; // Always hide elements (again) if no visual blocker is possible (get) and memorize
  // the "!important" state so we can correctly reset it in `probablyResetParentContainerForVisual`

  var fnStrictHide = function fnStrictHide() {
    if (["script", "link"].indexOf(node === null || node === void 0 ? void 0 : node.tagName.toLowerCase()) === -1 && visualParentMode !== "childrenSelector") {
      var style = node.style;

      if (style.getPropertyValue("display") === "none" && style.getPropertyPriority("display") === "important") {
        node.setAttribute(HTML_ATTRIBUTE_WAS_PREVIOUSLY_STRICT_HIDDEN, "1");
      } else {
        style.setProperty("display", "none", "important");
      }
    }
  };

  if (isNotInContent || isPositioned && !isPotentialVisualWithinResponsiveContainer(node) && !forceHidden || !visual || isAlreadyConnected || !isVisible && !forceHidden) {
    // Check if this content blocker is inside a known container for which we need to wait first its visibility
    if (!isVisible && dependantVisibilityContainers) {
      var dependantContainers = parents(node, dependantVisibilityContainers.join(","));

      if (dependantContainers.length > 0 && !dependantContainers[0].offsetParent) {
        // Wait for visibility, do nothing with this node...
        return;
      }
    }

    fnStrictHide();
    return;
  } // Avoid duplicate visual blockers and connect them


  var foundDuplicate = findPreviousVisibleDuplicate(createBefore, blockerId);

  if (foundDuplicate) {
    node.setAttribute(HTML_ATTRIBUTE_BLOCKER_CONNECTED, foundDuplicate.getAttribute(HTML_ATTRIBUTE_BLOCKER_CONNECTED));
    probablyResetParentContainerForVisual(createBefore, true);
    fnStrictHide();
    return;
  }

  var _createVisualContaine = createVisualContainer(node, createBefore, visualParentMode),
      container = _createVisualContaine.container,
      thumbnail = _createVisualContaine.thumbnail;

  var mountVisualContainer = function mountVisualContainer(paintMode) {
    container.setAttribute(HTML_ATTRIBUTE_VISUAL_PAINT_MODE, paintMode);
    mount({
      container: container,
      blocker: blocker,
      connectedCounter: connectedCounter,
      onClick: function onClick(event) {
        // Stop above propagation so click event handlers do not handle this as a click on the
        // mainly blocked element (example: Video inside another container which delegates click event).
        event === null || event === void 0 ? void 0 : event.stopPropagation();
        setLastClickedConnectedCounter(connectedCounter);
      },
      blockedNode: node,
      thumbnail: thumbnail,
      paintMode: paintMode
    });
    probablyResetParentContainerForVisual(createBefore, true);
  }; // Lazy load the visual container (not depending on view port but do not overload the main thread with idle timing)


  if (isElementInViewport(container, true)) {
    mountVisualContainer("instantInViewport");
  } else if ( // If the previous one was in viewport, consider the next one as "instant" as it could lead
  // to unexpected behavior (e.g. ratio class is used and container is in viewport at the bottom screen
  // but isn't technically due to vertical alignment to bottom).
  ((_document$querySelect = document.querySelector(".rcb-content-blocker[".concat(HTML_ATTRIBUTE_BLOCKER_CONNECTED, "=\"").concat(connectedCounter - 1, "\"][").concat(HTML_ATTRIBUTE_VISUAL_PAINT_MODE, "]"))) === null || _document$querySelect === void 0 ? void 0 : _document$querySelect.getAttribute(HTML_ATTRIBUTE_VISUAL_PAINT_MODE)) === "instantInViewport") {
    mountVisualContainer("instant");
  } else if (window.requestIdleCallback) {
    window.requestIdleCallback(function () {
      return mountVisualContainer("idleCallback");
    });
  } else {
    setTimeout(function () {
      return mountVisualContainer("instant");
    });
  }
}

function getLastClickedConnectedCounter() {
  return lastClickedConnectedCounter;
}

function setLastClickedConnectedCounter(connectedCounter) {
  lastClickedConnectedCounter = connectedCounter;
}

export { createVisual, getLastClickedConnectedCounter, setLastClickedConnectedCounter };